/**
 * Echobot Notification
 * Visual notification system with Echobot animation
 * Alternative to standard toasts
 */

import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { EchobotAnimation } from '../../types/echobot';

export interface EchobotNotificationProps {
  message: string;
  type?: 'success' | 'error' | 'info' | 'warning';
  duration?: number;
  onClose: () => void;
  animation?: EchobotAnimation;
}

const EchobotNotification: React.FC<EchobotNotificationProps> = ({
  message,
  type = 'info',
  duration = 4000,
  onClose,
  animation
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentAnimation, setCurrentAnimation] = useState<EchobotAnimation>(
    animation || getDefaultAnimation(type)
  );

  // Determine animation based on type
  function getDefaultAnimation(notifType: string): EchobotAnimation {
    switch (notifType) {
      case 'success':
        return 'celebrate';
      case 'error':
        return 'shake';
      case 'warning':
        return 'question';
      default:
        return 'wave';
    }
  }

  // Slide in animation
  useEffect(() => {
    setTimeout(() => setIsVisible(true), 10);
    
    if (duration > 0) {
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 300);
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration, onClose]);

  // Play animation on mount
  useEffect(() => {
    setCurrentAnimation(animation || getDefaultAnimation(type));
  }, [type, animation]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  // Color scheme based on type
  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'from-success to-success',
          border: 'border-success',
          text: 'text-success',
          icon: 'text-success'
        };
      case 'error':
        return {
          bg: 'from-danger to-accent',
          border: 'border-danger',
          text: 'text-danger',
          icon: 'text-danger'
        };
      case 'warning':
        return {
          bg: 'from-warning to-warning',
          border: 'border-warning',
          text: 'text-warning',
          icon: 'text-warning'
        };
      default:
        return {
          bg: 'from-accent to-info',
          border: 'border-accent',
          text: 'text-info',
          icon: 'text-accent'
        };
    }
  };

  const colors = getColors();

  return (
    <div
      className={`fixed bottom-6 right-6 z-[9999] transition-all duration-300 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
      }`}
    >
      <div className={`bg-gradient-to-br ${colors.bg} border ${colors.border} rounded-2xl shadow-heavy p-4 min-w-[320px] max-w-md flex items-start gap-3`}>
        {/* Echobot Icon with Animation */}
        <div className="flex-shrink-0">
          <div
            className={`w-12 h-12 rounded-full bg-gradient-to-br from-accent to-info flex items-center justify-center text-txt-inverted text-xl ${
              currentAnimation !== 'none' ? `animate-${currentAnimation}` : ''
            }`}
          >
            🤖
          </div>
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium ${colors.text}`}>
            {message}
          </p>
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className={`flex-shrink-0 p-1 hover:bg-bg-card/50 rounded-lg transition-colors ${colors.icon}`}
          aria-label="Close notification"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Progress Bar */}
        {duration > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/30 rounded-b-2xl overflow-hidden">
            <div
              className={`h-full ${colors.icon === 'text-success' ? 'bg-success' :
                colors.icon === 'text-danger' ? 'bg-danger' :
                colors.icon === 'text-warning' ? 'bg-warning' :
                'bg-info'
              }`}
              style={{
                animation: `shrink ${duration}ms linear forwards`
              }}
            />
          </div>
        )}
      </div>

      <style>{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }

        @keyframes animate-celebrate {
          0%, 100% { transform: scale(1) rotate(0deg); }
          25% { transform: scale(1.1) rotate(-10deg); }
          75% { transform: scale(1.1) rotate(10deg); }
        }

        @keyframes animate-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        @keyframes animate-question {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }

        @keyframes animate-wave {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        .animate-celebrate {
          animation: animate-celebrate 0.5s ease-in-out 3;
        }

        .animate-shake {
          animation: animate-shake 0.3s ease-in-out 3;
        }

        .animate-question {
          animation: animate-question 0.5s ease-in-out 2;
        }

        .animate-wave {
          animation: animate-wave 1s ease-in-out 2;
        }
      `}</style>
    </div>
  );
};

export default EchobotNotification;

